import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { formatAmount, formatDate, formatDateRange } from '@/lib/venue/format'
import type { VenueManagerSettlementCycleView } from '@/lib/venue/settlementTypes'
import { SETTLEMENT_CYCLE_STATUS_TONE } from '@/lib/venue/settlementViewModel'

const COLUMNS = ['회차', '정산 기준 기간', '이용 완료 예약 수', '취소·환불 차감', '확정 금액', '지급일', '상태']

function CycleRow({ cycle }: { cycle: VenueManagerSettlementCycleView }) {
  return (
    <tr className="border-b border-slateish-200 last:border-b-0">
      <td className="whitespace-nowrap px-3 py-2 text-ink-700">
        {cycle.yearMonth}
        {/* 🚨 estimated=true 는 실시간 집계(조회마다 달라질 수 있다) — "예정" 배지로 알린다. */}
        {cycle.estimated ? (
          <span className="ml-1 rounded-full bg-slateish-200 px-1.5 py-0.5 text-[11px] text-slateish-600">
            예정
          </span>
        ) : null}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-slateish-600">{formatDateRange(cycle.from, cycle.to)}</td>
      <td className="px-3 py-2 text-right text-ink-700">
        {cycle.completedReservationCount.toLocaleString('ko-KR')}건
      </td>
      <td className="px-3 py-2 text-right text-danger-500">-{formatAmount(cycle.refundDeductedAmount)}</td>
      <td className="px-3 py-2 text-right font-medium text-ink-700">{formatAmount(cycle.settlementAmount)}</td>
      <td className="whitespace-nowrap px-3 py-2 text-slateish-600">{formatDate(cycle.payoutDate)}</td>
      <td className="px-3 py-2">
        <Badge tone={SETTLEMENT_CYCLE_STATUS_TONE[cycle.status]}>{cycle.statusLabel}</Badge>
      </td>
    </tr>
  )
}

/** 정산 확정·예정 목록 표 + "다음 정산 예정" 카드(설계 §2). */
export function SettlementCycleListTable({
  cycles,
  next,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  cycles: VenueManagerSettlementCycleView[]
  next: VenueManagerSettlementCycleView | null
  hasNextPage: boolean
  isFetchingNextPage: boolean
  onLoadMore: () => void
}) {
  return (
    <div className="space-y-4">
      {next ? (
        <Card>
          <CardHeader
            title="다음 정산 예정"
            action={<Badge tone={SETTLEMENT_CYCLE_STATUS_TONE[next.status]}>{next.statusLabel}</Badge>}
          />
          <CardBody>
            <p className="text-lg font-semibold text-ink-700">{formatAmount(next.estimatedPayoutAmount)}</p>
            <p className="mt-1 text-sm text-slateish-600">
              {next.yearMonth} 회차 · {formatDate(next.payoutDate)} 지급
            </p>
          </CardBody>
        </Card>
      ) : null}

      {cycles.length === 0 ? (
        <EmptyState title="조회된 정산 회차가 없습니다" />
      ) : (
        <div className="space-y-3">
          <div className="overflow-x-auto rounded-xl border border-slateish-200">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-slateish-200 bg-slateish-50 text-left text-xs font-medium text-slateish-600">
                  {COLUMNS.map((column) => (
                    <th key={column} scope="col" className="whitespace-nowrap px-3 py-2">
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cycles.map((cycle) => (
                  <CycleRow key={cycle.yearMonth} cycle={cycle} />
                ))}
              </tbody>
            </table>
          </div>

          {hasNextPage ? (
            <div className="flex justify-center">
              <Button variant="secondary" size="sm" disabled={isFetchingNextPage} onClick={onLoadMore}>
                {isFetchingNextPage ? '불러오는 중' : '더 보기'}
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
